/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { TableCell } from '@/components/blocks/Table/TableCell';
import { TableRow } from '@/components/blocks/Table/TableRow';
import { TableData } from '@/components/content/OrderItemTable';
import { OrderItemAvailability } from '@/components/content/OrderItemTable/parts/Availability';
import { OrderItemItemDetails } from '@/components/content/OrderItemTable/parts/ItemDetails';
import { OrderItemItemDetailsCompact } from '@/components/content/OrderItemTable/parts/ItemDetailsCompact';
import { OrderItemItemDetailsMini } from '@/components/content/OrderItemTable/parts/ItemDetailsMini';
import { OrderItemPrice } from '@/components/content/OrderItemTable/parts/Price';
import { OrderItemQuantity } from '@/components/content/OrderItemTable/parts/Quantity';
import {
	OrderItemTableRowContainerSX,
	orderItemTableRowSX,
} from '@/components/content/OrderItemTable/styles/orderItemTableRow';
import { useOrderItemTableRow } from '@/data/Content/OrderItemTable';
import { ContentContext, ContentProvider } from '@/data/context/content';
import { ProductType } from '@/data/types/Product';
import { Switch } from '@/utils/switch';
import { Row } from '@tanstack/react-table';
import { useTrackSelectItem } from 'presentation/events/hooks/useTrackSelectItem';
import { FC, useContext, useMemo } from 'react';
import { useGetCategories } from 'shared/custom/hooks/Categories';
import { findParentCategories } from 'shared/custom/utils/getCategoryByIdForGA';
import {
	getMultiFreeGiftPromoCode,
	useMultiFreeGiftPromoDetails,
} from '@/data/Content/MultiFreeGiftPromoDetails';
import { useHasFreeGiftPromoIcon } from '@/data/utils/usePromoIcons';
import { MultiFreeGiftPromoContext } from '@/components/blocks/PLPProductCard/context/MultiFreeGiftPromoContext';
export const OrderItemTableRow: FC<{
	row: Row<TableData>;
	lastRow?: boolean;
	/** Product coming from the table, already fetched in bulk for every row. */
	product?: ProductType;
	productsLoading?: boolean;
}> = ({ row, lastRow = false, product, productsLoading = false }) => {
	const { partNumber, contractId, orderItemId } = row.original.itemDetails;
	const { details } = useOrderItemTableRow(partNumber, contractId, orderItemId, {
		product,
		loading: productsLoading,
	});
	const { view, readOnly } = useContext(ContentContext) as {
		view: string;
		readOnly: boolean;
	};
	const { navigation } = useGetCategories();
	const categories = useMemo(() => {
		if (!product?.parentCatalogGroupID) return [];
		return findParentCategories(navigation, product.parentCatalogGroupID);
	}, [navigation, product?.parentCatalogGroupID]);

	const batchedPromoMap = useContext(MultiFreeGiftPromoContext);
	const isBatched = batchedPromoMap !== null;
	const productId = product?.id ? product.id : '';

	const hasFreeGiftIcon = useHasFreeGiftPromoIcon(product);
	const { data: multiFreeGiftPromo } = useMultiFreeGiftPromoDetails(
		!isBatched && hasFreeGiftIcon ? productId : ''
	);
	// Free-gift variant (PGC/PGNC/TNC-PGC/TNC-PGNC/PG) for the select_item promo_thd.
	const promoThdCode = isBatched
		? batchedPromoMap?.get(productId)
		: getMultiFreeGiftPromoCode(multiFreeGiftPromo, productId);

	const trackSelectItem = useTrackSelectItem();
	const gtmSelectItem = () => {
		if (product) {
			trackSelectItem({
				product: { ...product, promoThdCode },
				categories,
			});
		}
	};

	const rowValues = useMemo(
		() => ({
			...row.original,
			details,
			product,
			productsLoading,
		}),
		[details, row.original, product, productsLoading]
	);
	const { freeGift } = row.original;
	const isReadOnly = freeGift || readOnly;

	return (
		<ContentProvider value={rowValues}>
			<TableRow
				id={`order-item-table-row-${row.id}`}
				data-testid={`order-item-table-row-${row.id}`}
				sx={orderItemTableRowSX}
				className="quick-cart__product-container"
			>
				{row.getVisibleCells().map((cell) => (
					<TableCell
						key={`order-item-table-cell-${cell.id}`}
						id={`order-item-table-cell-${cell.id}`}
						data-testid={`order-item-table-cell-${cell.id}`}
						sx={OrderItemTableRowContainerSX(row.id, lastRow)}
					>
						{Switch(cell.column.id)
							.case('itemDetails', () =>
								view === 'mini' ? (
									<OrderItemItemDetailsMini gtmSelectItem={gtmSelectItem} />
								) : view === 'compact' ? (
									<OrderItemItemDetailsCompact
										gtmSelectItem={gtmSelectItem}
										readOnly={isReadOnly}
									/>
								) : (
									<OrderItemItemDetails gtmSelectItem={gtmSelectItem} />
								)
							)
							.case('availability', () => <OrderItemAvailability />)
							.case('quantity', () => <OrderItemQuantity readOnly={isReadOnly} />)
							.case('price', () => <OrderItemPrice />)
							.defaultTo(() => null)}
					</TableCell>
				))}
			</TableRow>
		</ContentProvider>
	);
};
